import { call, put, select } from 'redux-saga/effects';

import { goToBoard, goToCard , handleLocationChange} from './router';
import request from '../request';
import selectors from '../../../selectors';
import actions from '../../../actions';
import api from '../../../api';
import ActionTypes from '../../../constants/ActionTypes';
import { createLocalId } from '../../../utils/local-id';
import getCardTransferErrorMessage from '../../../utils/get-card-transfer-error-message';
import services from '.';

const FALLBACK_POSITION = 65535;

const resolveCardPosition = (position) => {
  if (typeof position === 'number' && !Number.isNaN(position)) {
    return position;
  }
  return FALLBACK_POSITION;
};

export function* createCard(listId, data, autoOpen) {
  const { boardId } = yield select(selectors.selectListById, listId);

  const nextData = {
    ...data,
    position: yield select(selectors.selectNextCardPosition, listId),
  };

  const localId = yield call(createLocalId);

  yield put(
    actions.createCard({
      ...nextData,
      boardId,
      listId,
      id: localId,
    }),
  );

  let card;
  try {
    ({ item: card } = yield call(request, api.createCard, listId, nextData));
  } catch (error) {
    yield put(actions.createCard.failure(localId, error));
    return;
  }

  yield put(actions.createCard.success(localId, card));

  if (autoOpen) {
    yield call(goToCard, card.id);
  }
}

export function* handleCardCreate(card) {
  yield put(actions.handleCardCreate(card));
}

export function* updateCard(id, data) {
  const prevCard = yield select(selectors.selectCardById, id);
  const isTransfer = !!(
    data &&
    data.boardId &&
    (!prevCard || String(data.boardId) !== String(prevCard.boardId))
  );

  yield put(actions.updateCard(id, data));

  let card;
  let included = {};
  try {
    ({ item: card, included = {} } = yield call(request, api.updateCard, id, data));
  } catch (error) {
    yield put(actions.updateCard.failure(id, error));
    return { error };
  }

  yield put(actions.updateCard.success(card));

  // После transfer оптимистично чистятся related — подтягиваем tasks/attachments заново
  if (isTransfer) {
    yield call(handleCardUpdate, card, true);

    if (included && included.board) {
      yield put(actions.handleBoardUpdate(included.board));
    }

    if (
      included &&
      ((included.cardPointData && included.cardPointData.length > 0) ||
        (included.cardPointAdvancedData && included.cardPointAdvancedData.length > 0) ||
        (included.cardPointArchivedData && included.cardPointArchivedData.length > 0))
    ) {
      const {
        cardPointData = [],
        cardPointAdvancedData = [],
        cardPointArchivedData = [],
      } = included;

      for (const item of cardPointData) {
        if (item && item.pointSistemId) {
          yield put(actions.fetchCardPointData.success(item.cardId, item.pointSistemId, item));
        }
      }

      if (cardPointAdvancedData.length > 0) {
        const byCardId = {};
        cardPointAdvancedData.forEach((item) => {
          if (!item || !item.cardId) {
            return;
          }
          const key = String(item.cardId);
          if (!byCardId[key]) {
            byCardId[key] = [];
          }
          byCardId[key].push(item);
        });
        // eslint-disable-next-line no-restricted-syntax
        for (const [cardKey, items] of Object.entries(byCardId)) {
          const pointSistemId = items[0] && items[0].pointSistemId;
          if (pointSistemId) {
            yield put(actions.fetchCardPointAdvancedData.success(cardKey, pointSistemId, items));
          }
        }
      }

      for (const item of cardPointArchivedData) {
        if (item) {
          yield put({
            type: ActionTypes.CARD_POINT_DATA_COMPLETE__SUCCESS,
            payload: {
              archivedItem: item,
            },
          });
        }
      }
    }
  }

  return { card, included };
}

export function* updateCurrentCard(data) {
  const { cardId } = yield select(selectors.selectPath);
  
  yield call(updateCard, cardId, data);
}

export function* handleCardUpdate(card, forceFetch = false) {
  let fetch = forceFetch;
  if (!fetch && card.boardId) {
    const prevCard = yield select(selectors.selectCardById, card.id);
    fetch = !prevCard || String(prevCard.boardId) !== String(card.boardId);
  }

  let cardMemberships;
  let cardLabels;
  let tasks;
  let attachments;

  if (fetch) {
    try {
      ({
        item: card, // eslint-disable-line no-param-reassign
        included: { cardMemberships, cardLabels, tasks, attachments },
      } = yield call(request, api.getCard, card.id));
    } catch (error) {
      fetch = false;
    }
  }

  yield put(actions.handleCardUpdate(card, fetch, cardMemberships, cardLabels, tasks, attachments));
}

export function* moveCard(id, listId, index) {
  yield put(actions.cardTransfer(id));

  const position = resolveCardPosition(
    yield select(selectors.selectNextCardPosition, listId, index, id),
  );

  const result = yield call(updateCard, id, {
    listId,
    position,
  });

  if (result && result.error) {
    yield put(actions.cardTransfer.failure(id, getCardTransferErrorMessage(result.error)));
    return;
  }

  yield put(actions.cardTransfer.success(id));
}

export function* moveCurrentCard(listId, index) {
  const { cardId } = yield select(selectors.selectPath);

  yield call(moveCard, cardId, listId, index);
}

export function* transferCard(id, boardId, listId, index, pointSystemOptions = {}) {
  const { cardId: currentCardId, boardId: currentBoardId } = yield select(selectors.selectPath);

  yield put(actions.cardTransfer(id));

  const position = resolveCardPosition(
    yield select(selectors.selectNextCardPosition, listId, index, id),
  );

  // Pre-check: если целевая доска без PS и есть начисления — спрашиваем до переноса
  if (!pointSystemOptions.skipCheck) {
    let pointSystem = null;
    try {
      const response = yield call(request, api.checkCardTransfer, id, boardId);
      pointSystem = (response && response.item && response.item.pointSystem) || null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[transferCard] transfer-check failed:', error);
      yield put(actions.cardTransfer.failure(id, getCardTransferErrorMessage(error)));
      return;
    }

    if (pointSystem && pointSystem.actionHint === 'enable_and_apply') {
      yield put(actions.cardTransfer.success(id, { prompted: true }));
      yield put(
        actions.promptCardTransferPointSystem({
          mode: 'enable_and_apply',
          cardId: id,
          boardId,
          listId,
          index,
          templateId: pointSystem.sourceTemplate && pointSystem.sourceTemplate.id,
          templateName: (pointSystem.sourceTemplate && pointSystem.sourceTemplate.name) || '',
        }),
      );
      return;
    }
  }

  const data = {
    boardId,
    listId,
    position,
  };

  if (pointSystemOptions.enableAndApplyTemplateId) {
    data.enableAndApplyTemplateId = String(pointSystemOptions.enableAndApplyTemplateId);
  }

  const result = yield call(updateCard, id, data);

  if (result && result.error) {
    yield put(actions.cardTransfer.failure(id, getCardTransferErrorMessage(result.error)));
    return;
  }

  yield put(actions.cardTransfer.success(id));

  if (id === currentCardId) {
    yield call(goToBoard, currentBoardId);
  }
}

export function* transferCurrentCard(boardId, listId, index, pointSystemOptions = {}) {
  const { cardId } = yield select(selectors.selectPath);

  yield call(transferCard, cardId, boardId, listId, index, pointSystemOptions);
}

export function* confirmCardTransferPointSystem(enable) {
  const prompt = yield select(selectors.selectCardTransferPointSystemPrompt);
  if (!prompt || prompt.mode !== 'enable_and_apply') {
    return;
  }

  const {
    cardId,
    boardId,
    listId,
    index = 0,
    templateId,
  } = prompt;

  if (enable) {
    yield call(transferCard, cardId, boardId, listId, index, {
      skipCheck: true,
      enableAndApplyTemplateId: templateId,
    });
  } else {
    yield call(transferCard, cardId, boardId, listId, index, {
      skipCheck: true,
    });
  }

  const status = yield select(selectors.selectCardTransferStatus);
  if (status && status.status === 'success') {
    yield put(actions.clearCardTransferPointSystemPrompt());
  }
}

export function* deleteCard(id) {
  const { cardId, boardId } = yield select(selectors.selectPath);

  if (id === cardId) {
    yield call(goToBoard, boardId);
  }

  yield put(actions.deleteCard(id));

  let card;
  try {
    ({ item: card } = yield call(request, api.deleteCard, id));
  } catch (error) {
    yield put(actions.deleteCard.failure(id, error));
    return;
  }

  yield put(actions.deleteCard.success(card));
}

export function* archiveCard(id) {
  const { cardId, boardId } = yield select(selectors.selectPath);
  if (id === cardId) {
    yield call(goToBoard, boardId);
  }
  
  yield put(actions.archiveCard(id));
  let card;
  try {
    ({ item: card } = yield call(request, api.archiveCard, id));
  } catch (error) {
    yield put(actions.archiveCard.failure(id, error));
    return;
  }
  
  yield put(actions.archiveCard.success(card));
}

export function* handleCardArchive(card) {
  const { cardId, boardId } = yield select(selectors.selectPath);
  if (card.id === cardId) {
    yield call(goToBoard, boardId);
  }
  
  yield put(actions.handleCardArchive(card));
}


export function* cloneCard(id) {
  const { cardId, boardId } = yield select(selectors.selectPath);

  if (id === cardId) {
    yield call(goToBoard, boardId);
  }

  yield put(actions.cloneCard(id));

  let card;
  try {
    yield call(request, api.cloneCard, id);
    //({ item: card } = yield call(request, api.cloneCard, id));
  } catch (error) {
    yield put(actions.cloneCard.failure(id, error));
    return;
  }
  yield put(actions.cloneCard.success());
}

export function* handleCardClone(card) {
  const { cardId, boardId } = yield select(selectors.selectPath);
  if (card.id === cardId) {
    yield call(goToBoard, boardId);
  }
  yield put(actions.handleCardClone());
}

export function* cloneCurrentCard() {
  const { cardId } = yield select(selectors.selectPath);

  yield call(cloneCard, cardId);
}



export function* restoreCard(id) {
  const { cardId, boardId } = yield select(selectors.selectPath);

  if (id === cardId) {
    yield call(goToBoard, boardId);
  }

  yield put(actions.restoreCard(id));

  let card;
  try {
    ({ item: card } = yield call(request, api.restoreCard, id));
  } catch (error) {
    yield put(actions.restoreCard.failure(id, error));
    return;
  }

  yield put(actions.restoreCard.success(card));
}

export function* handleCardRestore(card) {
  const { cardId, boardId } = yield select(selectors.selectPath);

  if (card.id === cardId) {
    yield call(goToBoard, boardId);
  }

  yield put(actions.handleCardRestore(card));
}




export function* deleteCurrentCard() {
  const { cardId } = yield select(selectors.selectPath);

  yield call(deleteCard, cardId);
}

export function* archiveCurrentCard() {
  const { cardId } = yield select(selectors.selectPath);

  yield call(archiveCard, cardId);
}


export function* handleCardDelete(card) {
  const { cardId, boardId } = yield select(selectors.selectPath);

  if (card.id === cardId) {
    yield call(goToBoard, boardId);
  }

  yield put(actions.handleCardDelete(card));
}

export default {
  createCard,
  handleCardCreate,
  updateCard,
  updateCurrentCard,
  moveCard,
  moveCurrentCard,
  transferCard,
  transferCurrentCard,
  confirmCardTransferPointSystem,
  handleCardUpdate,
  deleteCard,
  archiveCard,
  cloneCard,
  handleCardClone,
  archiveCurrentCard,
  deleteCurrentCard,
  handleCardDelete,
  handleCardArchive,
  restoreCard,
  handleCardRestore,
};
